import { IF, nil, NO, UITextView, UIView, YES } from "uicore-ts"
import { RHButton } from "../../Custom components/RH/RHButton"
import { RHColors, RHFont, RHRadius, RHSpacing } from "../../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHEuroFormatter } from "../../Custom components/RH/Utils_slop/RHEuroFormatter"
import { RHTextSanitizer } from "../../Custom components/RH/Utils_slop/RHTextSanitizer"
import { RHCPVSummationViewTokens } from "../../Custom components/RH/Utils_slop/RHViewTokens"
import {
    RHCPVSummationInputPart,
    RHCPVSummationLevelResult,
    RHCPVSummationReport,
    RHCPVSummationStatus
} from "../../SocketClientFunctions"
import { CPVSummationDetailsView } from "./CPVSummationDetailsView"


export class CPVSummationPartReportView extends UIView {

    readonly statusTitleLabel = new UITextView(undefined, UITextView.type.header5)
        .configuredWithObject({
            text: "CPV piirmäär on saavutatud",
            textColor: RHColors.textPrimary,
            style: {
                fontWeight: RHFont.weightSemibold
            }
        })
        .addedAsSubviewToView(this)
    
    readonly summaryLabel = new UITextView()
        .configuredWithObject({
            isSingleLine: NO,
            textColor: RHColors.textPrimary,
            style: {
                fontSize: RHFont.sizeSmall,
                fontWeight: RHFont.weightRegular,
                lineHeight: "1.35"
            }
        })
        .addedAsSubviewToView(this)
    
    readonly toggleButton = new RHButton()
        .configuredWithObject({
            titleLabel: {
                text: "Vaata arvestust",
                style: {
                    fontSize: RHFont.sizeSmall
                }
            },
            variant: "light",
            contentPadding: RHSpacing.small
        })
        .addedAsSubviewToView(this)

    readonly levelSelectionLabel = new UITextView()
        .configuredWithObject({
            text: "CPV-haru tasemed, kus piirmäär on saavutatud",
            isSingleLine: NO,
            textColor: RHColors.textSecondary,
            style: {
                fontSize: RHFont.sizeSmall,
                fontWeight: RHFont.weightSemibold
            }
        })
    
    readonly detailsView = new CPVSummationDetailsView().configuredWithObject({ backgroundColor: RHColors.white })
    _isExpanded = NO
    _hasReport = NO
    _isLoadingDetails = NO
    _detailsLoadSequence = 0
    _report?: RHCPVSummationReport
    _levels: RHCPVSummationLevelResult[] = []
    _levelButtons: RHButton[] = []
    
    constructor(elementID?: string) {
        super(elementID)
        
        // noinspection JSConstantReassignment
        this.toggleButton.controlEventTargetAccumulator.PrimaryActionTriggered = async () => {
            await this._toggleDetailsVisibility()
        }
        
    }
    
    updateWithReportForPart(
        report: RHCPVSummationReport | undefined,
        part: RHCPVSummationInputPart | undefined
    ): boolean {
        this.clearReport()
        if (!report || !part || report.templateSummary || report.scope.contractingAuthorityType === "outsideRHS") {
            return NO
        }
        const levels = CPVSummationDetailsView.levelsForPartInReport(part, report)
            .filter(level => level.status === "red")
            .sort((firstLevel, secondLevel) => firstLevel.level - secondLevel.level)
        if (levels.length === 0) {
            return NO
        }
        this._hasReport = YES
        this._report = report
        this._levels = levels
        if (levels.length > 1) {
            this._levelButtons = levels.map(level => new RHButton().configuredWithObject({
                variant: "softDanger", contentPadding: RHSpacing.small,
                titleLabel: {
                    text: `${level.level}. tase · ` + RHTextSanitizer.escapedHTML(level.cpvLabel || "CPV arvestus") +
                        ` · kokku ${RHEuroFormatter.formattedAmount(level.sumWithNew)}` +
                        ` · piirmäär ${RHEuroFormatter.formattedAmount(level.threshold)}`,
                    isSingleLine: NO,
                    textAlignment: UITextView.textAlignment.left,
                    style: { fontSize: RHFont.sizeSmall, lineHeight: "1.35" }
                },
                controlEventTargetAccumulator: {
                    PrimaryActionTriggered: () => this._selectLevel(level)
                }
            }))
        }
        this._selectLevel(CPVSummationDetailsView.levelWithHighestRatio(levels)!)
        this._syncToggleButton()
        this._applyStatusStyle("red")
        this.setNeedsLayoutUpToRootView()
        return YES
    }
    
    clearReport() {
        this._detailsLoadSequence++
        this._isLoadingDetails = NO
        this._hasReport = NO
        this._isExpanded = NO
        this._report = undefined
        this._levels = []
        this._levelButtons.forEach(button => button.removeFromSuperview())
        this._levelButtons = []
        this.statusTitleLabel.text = "CPV piirmäär on saavutatud"
        this.summaryLabel.text = ""
        this.detailsView.clearReport()
        this._updateDetailsVisibility()
        this._syncToggleButton()
        this.setNeedsLayoutUpToRootView()
    }
    
    _syncToggleButton() {
        const title = IF(this._isExpanded)(() => "Peida arvestus").ELSE(() => "Vaata arvestust")
        this.toggleButton.configureWithObject({
            enabled: this._hasReport && !this._isLoadingDetails,
            hoverText: title,
            titleLabel: {
                text: IF(this._isLoadingDetails)(() =>
                    `<svg style="vertical-align:middle" width="14" height="14" viewBox="0 0 16 16" role="status" aria-label="Laadin lepinguid"><circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" stroke-width="2" stroke-dasharray="28 10"><animateTransform attributeName="transform" type="rotate" from="0 8 8" to="360 8 8" dur="0.8s" repeatCount="indefinite"/></circle></svg>`
                ).ELSE(() => title)
            }
        })
        this.toggleButton.viewHTMLElement.setAttribute("aria-label", title)
        this.toggleButton.viewHTMLElement.setAttribute("aria-busy", String(this._isLoadingDetails))
        this.toggleButton.viewHTMLElement.setAttribute("aria-disabled", String(!this.toggleButton.enabled))
    }

    async _toggleDetailsVisibility() {
        if (!this._hasReport || this._isLoadingDetails) {
            return
        }
        if (this._isExpanded) {
            this._isExpanded = NO
            this._syncToggleButton()
            this._updateDetailsVisibility()
            this.setNeedsLayoutUpToRootView()
            return
        }

        const loadSequence = ++this._detailsLoadSequence
        this._isLoadingDetails = YES
        this._syncToggleButton()
        try {
            await this._waitForNextPaint()
            if (loadSequence !== this._detailsLoadSequence || !this.isMemberOfViewTree) {
                return
            }
            // Attaching exposes the initial child frames before the parent has measured the details.
            this.detailsView.configureWithObject({ alpha: 0, userInteractionEnabled: NO })
            this._isExpanded = YES
            this._updateDetailsVisibility()
            this.setNeedsLayoutUpToRootView()
            UIView.layoutViewsIfNeeded()
            if (loadSequence === this._detailsLoadSequence) {
                this.detailsView.configureWithObject({ alpha: 1, userInteractionEnabled: YES })
            }
            await this._waitForNextPaint()
        }
        catch (error) {
            if (loadSequence === this._detailsLoadSequence) {
                this._isExpanded = NO
                this._updateDetailsVisibility()
                console.error("CPV summation details failed:", error)
            }
        }
        finally {
            if (loadSequence === this._detailsLoadSequence) {
                this._isLoadingDetails = NO
                this._syncToggleButton()
                this.setNeedsLayoutUpToRootView()
            }
        }
    }

    _waitForNextPaint(): Promise<void> {
        // A single animation frame runs before paint and can still hide the spinner behind row measurement.
        return new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(() => resolve())))
    }
    
    _updateDetailsVisibility() {
        if (this._isExpanded) {
            if (this._levelButtons.length > 0) {
                this.levelSelectionLabel.addedAsSubviewToView(this)
            }
            this._levelButtons.forEach(button => button.addedAsSubviewToView(this))
            this.detailsView.addedAsSubviewToView(this)
        }
        else {
            this.levelSelectionLabel.removeFromSuperview()
            this._levelButtons.forEach(button => button.removeFromSuperview())
            this.detailsView.removeFromSuperview()
        }
    }

    _selectLevel(level: RHCPVSummationLevelResult) {
        if (!this._report) {
            return
        }
        this.statusTitleLabel.text = IF(level.previouslyExceeded)(
            () => "CPV piirmäär oli juba saavutatud"
        ).ELSE(
            () => "CPV piirmäär on saavutatud"
        )
        this.summaryLabel.text = this._statusDescriptionForLevel(level)
        this._levelButtons.forEach((button, index) => {
            const isSelected = this._levels[index]?.cpvAtLevel === level.cpvAtLevel
            button.viewHTMLElement.setAttribute("aria-pressed", String(isSelected))
        })
        this.detailsView.updateWithReportForLevel(this._report, level)
        this.setNeedsLayoutUpToRootView()
    }

    _statusDescriptionForLevel(level: RHCPVSummationLevelResult): string {
        const label = RHTextSanitizer.escapedHTML(level.cpvLabel || "Valitud CPV-tase")
        const total = RHEuroFormatter.formattedAmount(level.sumWithNew)
        const threshold = RHEuroFormatter.formattedAmount(level.threshold)
        if (level.previouslyExceeded) {
            return `${label} harus oli viimase 12 kuu lepingute summa piirmäära saavutanud juba enne seda hanget. ` +
                `Koos selle hankega on kogusumma ${total} (piirmäär ${threshold}). ` +
                "See on kontrollmärguanne, mitte automaatne rikkumise otsus. Vajab sisulist hinnangut."
        }
        const excess = level.sumWithNew - level.threshold
        if (Math.abs(excess) < 0.005) {
            return `Koos selle hankega jõuab ${label} haru kogusumma piirmäärani (${threshold}). ` +
                "See on kontrollmärguanne, mitte automaatne rikkumise otsus. Vajab sisulist hinnangut."
        }
        return `${label} harus on koos selle hankega ${total}, mis ületab ${threshold} piirmäära ` +
            `${RHEuroFormatter.formattedAmount(excess)} võrra. ` +
            "See on kontrollmärguanne, mitte automaatne rikkumise otsus. Vajab sisulist hinnangut."
    }

    get focusableViews(): UIView[] {
        const views: UIView[] = [this.toggleButton]
        if (this._isExpanded) {
            views.push(...this._levelButtons, this.detailsView.copyButton)
            for (const groupView of this.detailsView._groupViews) {
                views.push(groupView.toggleButton)
                if (groupView._isExpanded) {
                    views.push(groupView.dataView.tableView)
                }
                if (groupView.moreButton.superview === groupView) {
                    views.push(groupView.moreButton)
                }
            }
        }
        return views
    }
    
    _applyStatusStyle(status: RHCPVSummationStatus) {
        let backgroundColor = RHColors.successGreenSurfaceFaint
        let borderColor = RHColors.greenBorderAlpha34
        if (status === "red") {
            backgroundColor = RHColors.pinkGlass
            borderColor = RHColors.softDangerBorderAlpha30
        }
        if (status === "yellow") {
            backgroundColor = RHColors.warningAmberSurfaceDraft
            borderColor = RHColors.warningAmberBorderDraft
        }
        
        ;(this as CPVSummationPartReportView).configureWithObject({
            backgroundColor,
            style: {
                border: `1px solid ${borderColor.stringValue}`,
                borderRadius: `${RHRadius.medium}px`
            }
        })
    }
    
    
    override layoutSubviews() {
        super.layoutSubviews()
        
        const bounds = this.contentBoundsWithInset(RHSpacing.small)
        const titleFrame = bounds
            .rectangleWithHeight(this.statusTitleLabel)
            .assignedAsFrameOfView(this.statusTitleLabel)
        const contentFrame = titleFrame.rectangleForNextRow(RHSpacing.tiny)
        let headerFrame = contentFrame
        if (bounds.width < RHCPVSummationViewTokens.headerStacksBelowWidth) {
            const summaryFrame = contentFrame
                .rectangleWithHeight(this.summaryLabel)
                .assignedAsFrameOfView(this.summaryLabel)
            headerFrame = summaryFrame
                .rectangleForNextRow(RHSpacing.small, RHCPVSummationViewTokens.headerActionHeight)
                .rectangleWithWidth(Math.min(bounds.width, RHCPVSummationViewTokens.headerActionWidth))
                .assignedAsFrameOfView(this.toggleButton)
        }
        else {
            const summaryWidth = bounds.width - RHCPVSummationViewTokens.headerActionWidth - RHSpacing.medium
            const rowHeight = Math.max(
                RHCPVSummationViewTokens.headerActionHeight,
                this.summaryLabel.intrinsicContentHeight(summaryWidth)
            )
            headerFrame = contentFrame
                .rectangleWithHeight(rowHeight)
                .distributeViewsAlongWidth(
                    [this.summaryLabel, this.toggleButton],
                    undefined,
                    RHSpacing.medium,
                    [nil, RHCPVSummationViewTokens.headerActionWidth]
                )
        }
        let contentBottomFrame = headerFrame
        if (this._isExpanded && this.levelSelectionLabel.superview === this) {
            contentBottomFrame = contentBottomFrame
                .rectangleForNextRow(RHSpacing.medium, this.levelSelectionLabel)
                .assignedAsFrameOfView(this.levelSelectionLabel)
        }
        if (this._isExpanded && this._levelButtons.length > 0) {
            this._levelButtons.forEach((button, index) => {
                const indentation = index * RHCPVSummationViewTokens.levelButtonIndent
                const buttonWidth = bounds.width - indentation
                const buttonHeight = Math.max(
                    RHCPVSummationViewTokens.levelButtonMinimumHeight,
                    button.titleLabel.intrinsicContentHeight(
                        buttonWidth - button.contentPadding * 2
                    ) + button.contentPadding * 2
                )
                const buttonFrame = bounds
                    .rectangleWithY(contentBottomFrame.max.y + RHSpacing.small)
                    .rectangleWithHeight(buttonHeight)
                    .rectangleWithInsets(indentation, 0, 0, 0)
                    .assignedAsFrameOfView(button)
                contentBottomFrame = buttonFrame
                    .rectangleWithX(bounds.x)
                    .rectangleWithWidth(bounds.width)
            })
        }
        if (this._isExpanded) {
            contentBottomFrame
                .rectangleForNextRow(RHSpacing.medium, this.detailsView)
                .assignedAsFrameOfView(this.detailsView)
        }
    }
    
}
